import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { timeDifference } from "../utils/timeUtils";
import { SectionHeader } from "./redesign/Sections";

const CATEGORIES = [
  { value: "", label: "Të gjitha kategoritë" },
  { value: "Shqipëri", label: "Shqipëri" },
  { value: "Kosovë", label: "Kosovë" },
  { value: "Maqedoni", label: "Maqedoni" },
  { value: "Bota", label: "Botë" },
  { value: "Ekonomi", label: "Ekonomi" },
  { value: "Sport", label: "Sport" },
  { value: "Kulturë", label: "Kulturë" },
  { value: "Rozë", label: "Rozë" },
  { value: "Kronika", label: "Kronikë" },
];

// Normalize diacritics for backend (matches old SearchSidebar behaviour).
const normalizeCategory = (c) => {
  const map = {
    Kosovë: "Kosovë",
    Kosove: "Kosovë",
    Rozë: "Rozë",
    Roze: "Rozë",
    Kulturë: "Kulturë",
    Kultura: "Kulturë",
  };
  return map[c] || c;
};

export default function SearchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();

  const initialQ = params.get("q") || "";
  const initialCat = params.get("category") || "";

  const [keyword, setKeyword] = useState(initialQ);
  const [category, setCategory] = useState(initialCat);
  const [portal, setPortal] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // Focus the input when arriving from the header pill.
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  // Keep the URL in sync so the search is shareable / refresh-safe.
  useEffect(() => {
    const next = new URLSearchParams();
    if (keyword.trim()) next.set("q", keyword.trim());
    if (category) next.set("category", category);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword, category]);

  // Debounced live search.
  useEffect(() => {
    if (!keyword.trim()) {
      setResults([]);
      setCount(0);
      setError(null);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const qp = new URLSearchParams({ keyword: keyword.trim() });
        if (category) qp.set("category", normalizeCategory(category));
        if (portal.trim()) qp.set("portal", portal.trim());
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/search/advanced?${qp}`
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.success) {
          setResults(data.results || []);
          setCount(Number(data.count ?? (data.results || []).length));
        } else {
          throw new Error(data.error || "Search failed");
        }
      } catch (err) {
        console.error("Search error:", err);
        setError("Gabim gjatë kërkimit. Provoni përsëri.");
        setResults([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [keyword, category, portal]);

  const clear = () => {
    setKeyword("");
    setCategory("");
    setPortal("");
    setShowAdvanced(false);
    setResults([]);
    setCount(0);
    setError(null);
  };

  const heading = useMemo(() => {
    if (!keyword.trim()) return "Kërko në arkivë";
    if (loading) return `Po kërkohet "${keyword.trim()}"…`;
    return `${count} rezultate për "${keyword.trim()}"`;
  }, [keyword, loading, count]);

  return (
    <div>
      {/* TODO: restore "Kërko & pyet AI" once Pyet AI is wired up */}
      <SectionHeader title="Kërko në arkivë" />

      {/* Search controls */}
      <div className="rounded-card border border-line bg-surface p-5">
        <div className="flex items-center gap-2">
          <div className="flex flex-1 items-center gap-2 rounded-pill border border-line bg-bg px-4 py-2.5">
            <span aria-hidden className="text-sub">
              ⌕
            </span>
            <input
              ref={inputRef}
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Shkruani fjalën kyçe ose pyetjen tuaj…"
              className="w-full bg-transparent text-[14px] text-ink placeholder:text-sub focus:outline-none"
            />
          </div>
          {(keyword || category || portal) && (
            <button
              type="button"
              onClick={clear}
              className="rounded-pill border border-line bg-bg px-3 py-2 font-head text-[12px] font-semibold text-sub hover:border-brand hover:text-brand"
            >
              Pastro
            </button>
          )}
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-pill border border-line bg-bg px-3 py-2 text-[13px] text-ink focus:border-brand focus:outline-none"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="rounded-pill border border-line bg-bg px-3 py-2 font-head text-[12px] font-semibold text-sub hover:border-brand hover:text-brand"
          >
            {showAdvanced ? "Mbyll opsionet" : "Opsionet e avancuara"}
          </button>
        </div>

        {showAdvanced && (
          <div className="mt-3 rounded-card border border-line bg-bg p-3">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-sub">
              Portal
            </label>
            <input
              type="text"
              placeholder="p.sh. Gazeta Express"
              value={portal}
              onChange={(e) => setPortal(e.target.value)}
              className="mt-1 w-full rounded-pill border border-line bg-surface px-3 py-2 text-[13px] text-ink focus:border-brand focus:outline-none"
            />
          </div>
        )}
      </div>

      {/* Results */}
      <div className="mt-8">
        <SectionHeader title={heading} />
        {error && (
          <p className="rounded-card border border-line bg-ai-bg p-4 text-[13px] text-ai">
            {error}
          </p>
        )}
        {!error && !loading && keyword.trim() && results.length === 0 && (
          <p className="text-[14px] text-sub">
            Nuk u gjetën rezultate për "{keyword.trim()}"
            {category && ` në kategorinë "${category}"`}
            {portal && ` nga portali "${portal}"`}.
          </p>
        )}
        {!keyword.trim() && (
          <p className="text-[14px] text-sub">
            Shkruaj një fjalë kyçe lart për të kërkuar mes lajmeve të 36 orëve
            të fundit.
          </p>
        )}
        <ul className="flex flex-col">
          {results.map((r, i) => (
            <li
              key={`${r.link}-${i}`}
              className="border-t border-line py-4 first:border-t-0"
            >
              <a
                href={r.link}
                target="_blank"
                rel="noopener noreferrer"
                className="block hover:underline"
              >
                <h3 className="font-head text-[15.5px] font-bold leading-snug tracking-tight text-ink md:text-[17px]">
                  {r.title}
                </h3>
              </a>
              <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px] text-sub">
                <span className="font-head text-[11.5px] font-semibold text-brand">
                  {r.portal}
                </span>
                {r.fetch_date && <span>· {timeDifference(r.fetch_date)}</span>}
                {r.category && <span>· {r.category}</span>}
              </div>
              {r.summary && (
                <p className="mt-2 text-[13px] leading-relaxed text-sub">
                  {r.summary.split(" ").slice(0, 50).join(" ") + "…"}
                </p>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
